import React, { FunctionComponent } from "react";
import {
    Form,
    FormActions,
    FormDropdown,
    FormFields,
    FormTextField,
    Stack,
    mapToDropdownOption,
    Spinner
} from "sprint-common-web";

import { CourseOfActionReference } from "../../../common/types/CourseOfActionReference";
import { FixedBillingReference } from "../../../common/types/FixedBillingReferfence";
import { RejectionReasonReference } from "../../../common/types/RejectionReasonReference";
import { CourseOfActionFormFields } from "../forms/status/status-details/course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "../forms/status/status-details/course-of-action/useCourseOfActionFormFieldsValues";
import { FileRecordRejectionReasonFormInputs } from "./FileRecordRejectionReasonFormInputs";
import { useFileRecordRejectionReasonForm } from "./useFileRecordRejectionReasonForm";

export type FileRecordRejectionReasonFormProps = {
    onConfirm: (payload: {
        reason: RejectionReasonReference;
        courseOfAction: CourseOfActionReference;
        fixedBilling?: FixedBillingReference;
        details: string;
    }) => Promise<void>;
    onCancel: () => void;
};

export const FileRecordRejectionReasonForm: FunctionComponent<FileRecordRejectionReasonFormProps> = ({
    onConfirm,
    onCancel
}: FileRecordRejectionReasonFormProps) => {
    const {
        control,
        errors,
        formState,
        handleSubmit,
        reset,
        areRejectionReasonsLoading,
        rejectionReasons
    } = useFileRecordRejectionReasonForm();
    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();

    const handleSubmitButtonClick = async (inputs: FileRecordRejectionReasonFormInputs) => {
        const rejectionReason = rejectionReasons.find((reason) => reason.id === inputs.reasonId);
        const courseOfAction = courseOfActions.find((action) => action.id === inputs.courseOfActionId);
        const fixedBilling = inputs.fixedBillingId
            ? fixedBillings.find((billing) => billing.id === inputs.fixedBillingId)
            : undefined;

        if (rejectionReason && courseOfAction) {
            return onConfirm({
                reason: rejectionReason,
                courseOfAction,
                fixedBilling,
                details: inputs.details || ""
            }).then(() => reset());
        }
    };

    const handleCancelButtonClick = () => {
        onCancel();
        reset();
    };

    if (areRejectionReasonsLoading || areCourseOfActionsLoading || areFixedBillingsLoading) {
        return (
            <Stack
                styles={{
                    root: { padding: 10 }
                }}
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleSubmitButtonClick)}>
                <Stack verticalAlign="space-between">
                    <FormFields>
                        <FormDropdown
                            name="reasonId"
                            label="Reason"
                            options={rejectionReasons.map((reason) => mapToDropdownOption(reason))}
                            rules={{ required: "Select a reason" }}
                            control={control}
                            errors={errors}
                        />
                        <CourseOfActionFormFields
                            control={control}
                            errors={errors}
                            courseOfActions={courseOfActions}
                            fixedBillings={fixedBillings}
                        />
                        <FormTextField
                            name="details"
                            label="Details"
                            control={control}
                            errors={errors}
                            multiline={true}
                        />
                    </FormFields>
                    <FormActions
                        isLoading={
                            areRejectionReasonsLoading ||
                            areCourseOfActionsLoading ||
                            areFixedBillingsLoading ||
                            formState.isSubmitting ||
                            formState.isValidating
                        }
                        submitDisabled={formState.isSubmitting || formState.isValidating}
                        onCancel={handleCancelButtonClick}
                    />
                </Stack>
            </Form>
        </Stack>
    );
};
